// src/screens/PlayerSeasonStats.tsx
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { ShotHeatmapCanvas } from "./ShotHeatmapCanvas";
import { CourtZoneSVGOverlay } from "./CourtZoneSVGOverlay";
import { Info, Lock, BarChart2, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip } from "../components/Tooltip";
import { ExportButtons } from "../components/ExportButtons";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader";
import { BackButton } from "../components/BackButton";


// --- mini hook i18n safe (évite TS2589) ---
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null
      ? String((i18n.t as any)(key, options))
      : String((i18n.t as any)(key));
  return { t, lng };
}

type Shot = { x: number; y: number; made: boolean; player_number: number };

type PlayerStat = {
  player_number: number;
  player_name: string;
  position: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_attempted: number;
  ft_made: number;
  three_attempted: number;
  three_made: number;
  games: number;
};

type TeamLite = { id: string; team_name: string | null };

export const PlayerSeasonStats = () => {
  const { t } = useI18nSafe();

  const [allStats, setAllStats] = useState<PlayerStat[]>([]);
  const [filteredStats, setFilteredStats] = useState<PlayerStat[]>([]);
  const [search, setSearch] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerStat | null>(null);
  const [activeTab, setActiveTab] = useState<"stats" | "heatmap">("stats");

  const [allShots, setAllShots] = useState<Shot[]>([]);
  const [selectedPlayerNumber, setSelectedPlayerNumber] = useState<string>("");
  const filteredShots =
    selectedPlayerNumber === ""
      ? allShots
      : allShots.filter((s) => s.player_number.toString() === selectedPlayerNumber);

  const [heatmapSubTab, setHeatmapSubTab] = useState<"tirs" | "zones">("tirs");
  const [isPremium, setIsPremium] = useState(false);

  const [teams, setTeams] = useState<TeamLite[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true); // 👈 AJOUT
  const MIN_SHOTS = 20;

  // Premium
  useEffect(() => {
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();
      if (data?.is_premium) setIsPremium(true);
    })();
  }, []);

  // Charger les équipes visibles (owner + membre actif)
  useEffect(() => {
    (async () => {
      setLoading(true); // on commence par charger au moins les équipes

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setTeams([]);
        setSelectedTeamId(null);
        setLoading(false);
        return;
      }
      const uid = user.id;

      const { data: owned, error: ownedErr } = await supabase
        .from("teams")
        .select("id, team_name")
        .eq("user_id", uid)
        .or("archived.is.null,archived.eq.false");
      if (ownedErr) console.error("[seasonstats] owned teams error:", ownedErr);

      const { data: memberRows, error: memberErr } = await supabase
        .from("team_members")
        .select("team_id, status, teams!inner(id, team_name, archived)")
        .eq("user_id", uid)
        .eq("status", "active");
      if (memberErr) console.error("[seasonstats] member teams error:", memberErr);

      const memberTeams: TeamLite[] = (memberRows || [])
        .map((r: any) => r.teams)
        .filter((t: any) => t && t.id && !t.archived);

      // merge unique
      const byId = new Map<string, TeamLite>();
      (owned || []).forEach((t) =>
        byId.set(t.id, { id: t.id, team_name: t.team_name })
      );
      memberTeams.forEach((t) =>
        byId.set(t.id, { id: t.id, team_name: t.team_name })
      );

      const merged = Array.from(byId.values()).sort((a, b) =>
        (a.team_name || "").localeCompare(b.team_name || "")
      );

      setTeams(merged);
      if (merged.length > 0) {
        setSelectedTeamId((prev) => prev ?? merged[0].id);
        // on garde loading à true, les stats vont le remettre à false
      } else {
        // aucune équipe : on arrête le loader et on affichera juste un tableau vide
        setSelectedTeamId(null);
        setLoading(false);
      }
    })();
  }, []);

  // Charger les tirs (par team_id)
  useEffect(() => {
    (async () => {
      if (!selectedTeamId) {
        setAllShots([]);
        return;
      }

      const { data: matchList, error: matchError } = await supabase
        .from("matches")
        .select("id")
        .eq("team_id", selectedTeamId);
      if (matchError || !matchList) {
        console.error("Erreur matchList:", matchError);
        setAllShots([]);
        return;
      }

      const matchIds = matchList.map((m) => m.id);
      if (matchIds.length === 0) {
        setAllShots([]);
        return;
      }

      const { data: shotsData, error: shotsError } = await supabase
        .from("shot_locations")
        .select("*")
        .in("match_id", matchIds);

      if (shotsError) {
        console.error("Erreur shot_locations:", shotsError);
        setAllShots([]);
        return;
      }

      setAllShots(
        (shotsData || []).map((s: any) => ({
          x: s.x,
          y: s.y,
          made: s.made,
          player_number: s.player_number,
        }))
      );
    })();
  }, [selectedTeamId]);

  // Charger les stats (par team_id)
  useEffect(() => {
    (async () => {
      if (!selectedTeamId) {
        setAllStats([]);
        setFilteredStats([]);
        return;
      }

      setLoading(true); // 👈 on relance le loader quand l'équipe change

      const { data: matchList, error: mErr } = await supabase
        .from("matches")
        .select("id")
        .eq("team_id", selectedTeamId);
      if (mErr) {
        console.error("Erreur matches:", mErr);
        setAllStats([]);
        setFilteredStats([]);
        setLoading(false);
        return;
      }
      const matchIds = (matchList || []).map((m) => m.id);
      if (matchIds.length === 0) {
        setAllStats([]);
        setFilteredStats([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("match_stats")
        .select("*")
        .in("match_id", matchIds);
      if (error) {
        console.error("Erreur match_stats:", error);
        setAllStats([]);
        setFilteredStats([]);
        setLoading(false);
        return;
      }

      const statsByPlayer: Record<string, PlayerStat> = {};
      (data || []).forEach((stat: any) => {
        const key = `${stat.player_number}-${stat.player_name}`;
        if (!statsByPlayer[key]) {
          statsByPlayer[key] = { ...stat, games: 1 };
        } else {
          statsByPlayer[key].points += stat.points;
          statsByPlayer[key].assists += stat.assists;
          statsByPlayer[key].rebounds += stat.rebounds;
          statsByPlayer[key].steals += stat.steals;
          statsByPlayer[key].blocks += stat.blocks;
          statsByPlayer[key].turnovers += stat.turnovers;
          statsByPlayer[key].shots_attempted += stat.shots_attempted;
          statsByPlayer[key].shots_made += stat.shots_made;
          statsByPlayer[key].ft_attempted += stat.ft_attempted;
          statsByPlayer[key].ft_made += stat.ft_made;
          statsByPlayer[key].three_attempted += stat.three_attempted;
          statsByPlayer[key].three_made += stat.three_made;
          statsByPlayer[key].games += 1;
        }
      });

      const finalStats = Object.values(statsByPlayer);
      setAllStats(finalStats);
      setFilteredStats(finalStats);
      setLoading(false); // ✅ stats prêtes, on enlève le loader
    })();
  }, [selectedTeamId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    setFilteredStats(
      allStats.filter(
        (s) =>
          s.player_name.toLowerCase().includes(value) ||
          s.player_number.toString().includes(value)
      )
    );
  };

  const calculateTS = (points: number, fga: number, fta: number): string => {
    const denominator = 2 * (fga + 0.44 * fta);
    if (denominator === 0) return "–";
    const ts = points / denominator;
    return (ts * 100).toFixed(1) + " %";
  };

  const calculateEFG = (fgm: number, fga: number, threeMade: number): string => {
    if (fga === 0) return "–";
    const efg = (fgm + 0.5 * threeMade) / fga;
    return (efg * 100).toFixed(1) + " %";
  };

  const calculateEFF = (s: PlayerStat): string => {
    const missedFG = s.shots_attempted - s.shots_made;
    const missedFT = s.ft_attempted - s.ft_made;
    const eff =
      (s.points +
        s.rebounds +
        s.assists +
        s.steals +
        s.blocks -
        s.turnovers -
        missedFG -
        missedFT) /
      s.games;
    return eff.toFixed(1);
  };

  // Totaux équipe
  const totalShotsMade = filteredStats.reduce(
    (sum, p) => sum + p.shots_made,
    0
  );
  const totalShotsAttempted = filteredStats.reduce(
    (sum, p) => sum + p.shots_attempted,
    0
  );
  const totalFTMade = filteredStats.reduce((sum, p) => sum + p.ft_made, 0);
  const totalFTAttempted = filteredStats.reduce(
    (sum, p) => sum + p.ft_attempted,
    0
  );
  const totalThreeMade = filteredStats.reduce(
    (sum, p) => sum + p.three_made,
    0
  );
  const totalThreeAttempted = filteredStats.reduce(
    (sum, p) => sum + p.three_attempted,
    0
  );
  const teamTotals = {
    points: filteredStats.reduce((sum, p) => sum + p.points, 0),
    shots_made: totalShotsMade,
    ft_made: totalFTMade,
    shots_attempted: totalShotsAttempted,
    ft_attempted: totalFTAttempted,
    rebounds: filteredStats.reduce((sum, p) => sum + p.rebounds, 0),
    assists: filteredStats.reduce((sum, p) => sum + p.assists, 0),
    steals: filteredStats.reduce((sum, p) => sum + p.steals, 0),
    blocks: filteredStats.reduce((sum, p) => sum + p.blocks, 0),
    turnovers: filteredStats.reduce((sum, p) => sum + p.turnovers, 0),
  };

  // 🔥 Loader global stats/heatmap
  if (loading) {
    return <LogoLoader />;
  }

  return (
    <section className="p-6 max-w-6xl mx-auto bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      {/* Header mobile sticky */}
      <div className="sticky top-0 z-50 w-full bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 mb-6">
        <div className="relative mx-auto max-w-6xl px-4 py-3">
          {/* Gauche: retour */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <BackButton />
          </div>

          {/* Droite: glossaire */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Link to="/glossaire-stats" title={t("seasonstats:glossary_title")}>
              <Info className="w-5 h-5 text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors" />
            </Link>
          </div>

          {/* Centre: titre */}
          <h1 className="text-lg font-bold text-orange-600 dark:text-orange-400 text-center">
            {t("seasonstats:title")}
          </h1>
        </div>
      </div>

      {/* Sélecteur d'équipe (owner + membre) */}
      <select
        value={selectedTeamId || ""}
        onChange={(e) => setSelectedTeamId(e.target.value)}
        className="mb-5 p-2 border border-gray-300 dark:border-gray-700 rounded font-semibold block mx-auto
                   bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
      >
        {teams.map((team) => (
          <option key={team.id} value={team.id}>
            {team.team_name ?? "—"}
          </option>
        ))}
      </select>

      {/* Tabs */}
      <div className="mb-6 flex justify-center gap-3">
        <button
          onClick={() => setActiveTab("stats")}
          className={`px-4 py-2 rounded font-semibold flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors
            ${activeTab === "stats"
              ? "bg-orange-600 text-white"
              : "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            }`}
        >
          <BarChart2 className="w-4 h-4" />
          {t("seasonstats:tab.stats")}
        </button>

        {isPremium ? (
          <button
            onClick={() => setActiveTab("heatmap")}
            className={`px-4 py-2 rounded font-semibold flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors
              ${activeTab === "heatmap"
                ? "bg-orange-600 text-white"
                : "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              }`}
          >
            <Flame className="w-4 h-4" />
            {t("seasonstats:tab.heatmap")}
          </button>
        ) : (
          <button
            disabled
            title={t("seasonstats:premium_only")}
            className="px-4 py-2 rounded font-semibold bg-gray-300 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center gap-2 cursor-not-allowed"
          >
            <Flame className="w-4 h-4" />
            {t("seasonstats:tab.heatmap")}
            <Lock className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Onglet STATS */}
      {activeTab === "stats" && (
        <>
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder={t("seasonstats:search_placeholder")}
            className="mb-6 w-full md:w-1/2 mx-auto block p-2 rounded border border-gray-300 dark:border-gray-700
                       bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                       focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
          />

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800/80">
                <tr>
                  <th className="p-2">{t("seasonstats:table.num")}</th>
                  <th className="p-2">{t("seasonstats:table.name")}</th>
                  <th className="p-2">{t("seasonstats:table.pos")}</th>
                  <th className="p-2">{t("seasonstats:table.games")}</th>
                  <th className="p-2">{t("seasonstats:table.pts")}</th>
                  <th className="p-2">{t("seasonstats:table.avg")}</th>
                  <th className="p-2">{t("seasonstats:table.ast")}</th>
                  <th className="p-2">{t("seasonstats:table.reb")}</th>
                  <th className="p-2">{t("seasonstats:table.stl")}</th>
                  <th className="p-2">{t("seasonstats:table.blk")}</th>
                  <th className="p-2">{t("seasonstats:table.to")}</th>
                  <th className="p-2">{t("seasonstats:table.fg")}</th>
                  <th className="p-2">{t("seasonstats:table.fg_pct")}</th>
                  <th className="p-2">{t("seasonstats:table.ft")}</th>
                  <th className="p-2">{t("seasonstats:table.tp")}</th>
                  <th className="p-2">{t("seasonstats:table.eval")}</th>
                  {isPremium && (
                    <th className="p-2">
                      <div className="flex items-center justify-center gap-1">
                        <span>eFG%</span>
                        <Tooltip text={t("seasonstats:tooltips.efg")} />
                      </div>
                    </th>
                  )}
                  {isPremium && (
                    <th className="p-2">
                      TS%
                      <Tooltip text={t("seasonstats:tooltips.ts")} />
                    </th>
                  )}
                  {isPremium && (
                    <th className="p-2">
                      EFF
                      <Tooltip text={t("seasonstats:tooltips.eff")} />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 text-center">
                {filteredStats.map((s, index) => (
                  <tr
                    key={index}
                    className="border-t border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    onClick={() => setSelectedPlayer(s)}
                  >
                    <td className="p-2">{s.player_number}</td>
                    <td className="p-2 text-orange-600 dark:text-orange-400 font-medium">
                      {s.player_name}
                    </td>
                    <td className="p-2">{s.position}</td>
                    <td className="p-2">{s.games}</td>
                    <td className="p-2">{s.points}</td>
                    <td className="p-2">
                      {(s.points / s.games).toFixed(1)}
                    </td>
                    <td className="p-2">{s.assists}</td>
                    <td className="p-2">{s.rebounds}</td>
                    <td className="p-2">{s.steals}</td>
                    <td className="p-2">{s.blocks}</td>
                    <td className="p-2">{s.turnovers}</td>
                    <td className="p-2">
                      {s.shots_made}/{s.shots_attempted}
                    </td>
                    <td className="p-2">
                      {s.shots_attempted > 0
                        ? `${(
                          (s.shots_made / s.shots_attempted) *
                          100
                        ).toFixed(1)}%`
                        : "0%"}
                    </td>
                    <td className="p-2">
                      {s.ft_made}/{s.ft_attempted}
                    </td>
                    <td className="p-2">
                      {s.three_made}/{s.three_attempted}
                    </td>
                    <td className="p-2">
                      {(
                        (s.points +
                          s.rebounds +
                          s.assists +
                          s.steals +
                          s.blocks -
                          s.turnovers +
                          s.shots_made -
                          s.shots_attempted) /
                        s.games
                      ).toFixed(1)}
                    </td>
                    {isPremium && (
                      <td className="p-2">
                        {calculateEFG(
                          s.shots_made,
                          s.shots_attempted,
                          s.three_made
                        )}
                      </td>
                    )}
                    {isPremium && (
                      <td className="p-2">
                        {calculateTS(
                          s.points,
                          s.shots_attempted,
                          s.ft_attempted
                        )}
                      </td>
                    )}
                    {isPremium && (
                      <td className="p-2">{calculateEFF(s)}</td>
                    )}
                  </tr>
                ))}

                {/* Totaux équipe */}
                <tr className="bg-gray-100 dark:bg-gray-800 font-semibold text-orange-600 dark:text-orange-400">
                  <td className="p-2" colSpan={11}>
                    {t("seasonstats:team_totals")}
                  </td>
                  <td className="p-2 text-center">
                    {totalShotsMade}/{totalShotsAttempted}
                  </td>
                  <td className="p-2 text-center">
                    {totalShotsAttempted > 0
                      ? `${(
                        (totalShotsMade / totalShotsAttempted) *
                        100
                      ).toFixed(1)}%`
                      : "0%"}
                  </td>
                  <td className="p-2 text-center">
                    {totalFTMade}/{totalFTAttempted}
                  </td>
                  <td className="p-2 text-center">
                    {totalThreeMade}/{totalThreeAttempted}
                  </td>
                  <td className="p-2 text-center">–</td>
                  {isPremium && (
                    <td className="p-2 text-center">
                      {totalShotsAttempted > 0
                        ? `${(
                          ((totalShotsMade +
                            0.5 * totalThreeMade) /
                            totalShotsAttempted) *
                          100
                        ).toFixed(1)}%`
                        : "–"}
                    </td>
                  )}
                  {isPremium && (
                    <td className="p-2 text-center">
                      {calculateTS(
                        teamTotals.points,
                        teamTotals.shots_attempted,
                        teamTotals.ft_attempted
                      )}
                    </td>
                  )}
                  {isPremium && (
                    <td className="p-2 text-center">–</td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>

          <ExportButtons
            data={filteredStats}
            fileName={t("seasonstats:export.filename")}
            excelColumns={{
              [t("seasonstats:export.excel.num")]: "player_number",
              [t("seasonstats:export.excel.name")]: "player_name",
              [t("seasonstats:export.excel.pos")]: "position",
              [t("seasonstats:export.excel.games")]: "games",
              [t("seasonstats:export.excel.points")]: "points",
              [t("seasonstats:export.excel.assists")]: "assists",
              [t("seasonstats:export.excel.rebounds")]: "rebounds",
              [t("seasonstats:export.excel.steals")]: "steals",
              [t("seasonstats:export.excel.blocks")]: "blocks",
              [t("seasonstats:export.excel.turnovers")]: "turnovers",
              [t("seasonstats:export.excel.fgm")]: "shots_made",
              [t("seasonstats:export.excel.fga")]: "shots_attempted",
              [t("seasonstats:export.excel.ftm")]: "ft_made",
              [t("seasonstats:export.excel.fta")]: "ft_attempted",
              [t("seasonstats:export.excel.tpm")]: "three_made",
              [t("seasonstats:export.excel.tpa")]: "three_attempted",
            }}
            pdfColumns={[
              {
                header: t("seasonstats:export.pdf.num"),
                key: "player_number",
              },
              {
                header: t("seasonstats:export.pdf.name"),
                key: "player_name",
              },
              {
                header: t("seasonstats:export.pdf.pos"),
                key: "position",
              },
              {
                header: t("seasonstats:export.pdf.games"),
                key: "games",
              },
              {
                header: t("seasonstats:export.pdf.pts"),
                key: "points",
              },
              {
                header: t("seasonstats:export.pdf.ast"),
                key: "assists",
              },
              {
                header: t("seasonstats:export.pdf.reb"),
                key: "rebounds",
              },
              {
                header: t("seasonstats:export.pdf.stl"),
                key: "steals",
              },
              {
                header: t("seasonstats:export.pdf.blk"),
                key: "blocks",
              },
              {
                header: t("seasonstats:export.pdf.to"),
                key: "turnovers",
              },
              {
                header: t("seasonstats:export.pdf.fgm"),
                key: "shots_made",
              },
              {
                header: t("seasonstats:export.pdf.fga"),
                key: "shots_attempted",
              },
            ]}
          />
        </>
      )}

      {/* Onglet HEATMAP */}
      {activeTab === "heatmap" && (
        <div className="mt-10">
          <select
            value={selectedPlayerNumber}
            onChange={(e) => setSelectedPlayerNumber(e.target.value)}
            className="mb-6 w-full md:w-1/2 mx-auto block p-2 rounded border border-gray-300 dark:border-gray-700
                       bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-center font-medium
                       focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
          >
            <option value="">{t("seasonstats:heatmap.all_team")}</option>
            {allStats.map((player) => (
              <option
                key={player.player_number}
                value={player.player_number.toString()}
              >
                {player.player_number} – {player.player_name}
              </option>
            ))}
          </select>

          <div className="flex justify-center mb-6 gap-3">
            <button
              onClick={() => setHeatmapSubTab("tirs")}
              className={`px-4 py-2 rounded font-semibold flex items-center gap-2 transition-colors
                ${heatmapSubTab === "tirs"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                }`}
            >
              <Flame className="w-4 h-4" />
              {t("seasonstats:heatmap.tab_shots")}
            </button>
            <button
              onClick={() => setHeatmapSubTab("zones")}
              className={`px-4 py-2 rounded font-semibold flex items-center gap-2 transition-colors
                ${heatmapSubTab === "zones"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                }`}
            >
              <BarChart2 className="w-4 h-4" />
              {t("seasonstats:heatmap.tab_zones")}
            </button>
          </div>

          {filteredShots.length < MIN_SHOTS && (
            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t("seasonstats:heatmap.min_shots", { min: MIN_SHOTS })}
            </p>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
            {heatmapSubTab === "tirs" ? (
              <ShotHeatmapCanvas
                shots={
                  selectedPlayerNumber === ""
                    ? allShots
                    : allShots.filter(
                      (s) => s.player_number.toString() === selectedPlayerNumber
                    )
                }
              />
            ) : (
              <CourtZoneSVGOverlay shots={filteredShots} />
            )}
          </div>
        </div>
      )}

      {/* Modale Radar */}
      <AnimatePresence>
        {selectedPlayer && (
          <motion.div
            className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPlayer(null)}
          >
            <motion.div
              className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-2xl shadow-lg p-6 w-full max-w-md relative border border-gray-200 dark:border-gray-800"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold text-center mb-4">
                {t("seasonstats:radar.title", {
                  name: selectedPlayer.player_name,
                })}
              </h2>

              <div className="text-gray-900 dark:text-gray-100">
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart
                    data={[
                      {
                        stat: t("seasonstats:radar.points"),
                        value:
                          selectedPlayer.points / selectedPlayer.games,
                      },
                      {
                        stat: t("seasonstats:radar.assists"),
                        value:
                          selectedPlayer.assists / selectedPlayer.games,
                      },
                      {
                        stat: t("seasonstats:radar.rebounds"),
                        value:
                          selectedPlayer.rebounds / selectedPlayer.games,
                      },
                      {
                        stat: t("seasonstats:radar.steals"),
                        value:
                          selectedPlayer.steals / selectedPlayer.games,
                      },
                      {
                        stat: t("seasonstats:radar.blocks"),
                        value:
                          selectedPlayer.blocks / selectedPlayer.games,
                      },
                      {
                        stat: t("seasonstats:radar.turnovers"),
                        value:
                          selectedPlayer.turnovers / selectedPlayer.games,
                      },
                    ]}
                  >
                    <PolarGrid
                      stroke="currentColor"
                      strokeOpacity={0.2}
                    />
                    <PolarAngleAxis
                      dataKey="stat"
                      tick={{ fill: "currentColor" }}
                    />
                    <PolarRadiusAxis
                      angle={30}
                      domain={[0, 10]}
                      tick={{ fill: "currentColor" }}
                    />
                    <Radar
                      dataKey="value"
                      stroke="#fc7825"
                      fill="#fc7825"
                      fillOpacity={0.6}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <button
                onClick={() => setSelectedPlayer(null)}
                className="mt-4 w-full bg-gray-800 dark:bg-gray-800 text-white py-2 rounded hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
              >
                {t("seasonstats:close")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
